import { ref, computed } from "@vue/composition-api";

export function useFacetedSearch(emit, props) {
  const selectedField = ref(null);
  const searchValue = ref('');
  const inputRef = ref(null);
  const uniqueId = Date.now().toString(36) + Math.random().toString(36).slice(2); 
  const hiddenOptionsSelect = ref(null);
  const facetedSerachContainer = ref(null);
  const selectedOptions = ref(null);
  const fieldSelector = ref(null);
  const searchValueFieldSelector = ref('');

  const buildFiltersArray = value => {
    if (!value || typeof value !== 'object') {
      return [];
    }

    return Object.entries(value)
      .filter(([, filterValue]) => {
        return filterValue !== null &&
          filterValue !== undefined &&
          filterValue !== '';
      })
      .map(([field, filterValue]) => {
        const option = props.options.find(item => {
          return item.value === field;
        });

        return {
          field,
          label: option?.label || field,
          value: filterValue,
          operator: option?.operator || 'contains',
        };
      });
  };

  const filtersToObject = filterList => {
    return filterList.reduce((result, filter) => {
      const key = filter.isArray
        ? `${filter.field}[]`
        : filter.field;

      result[key] = filter.value;

      return result;
    }, {});
  };

  const filters = ref(buildFiltersArray(props.value));

  const selectedOption = computed(() => {
    if (!selectedField.value) {
      return null;
    }

    const fieldValue =
      typeof selectedField.value === 'object'
        ? selectedField.value.value
        : selectedField.value;

    return props.options.find(option => {
      return option.value === fieldValue;
    }) || null;
  });

  const availableOptions = computed(() => {
    if (props.allowDuplicateFields) {
      return props.options;
    }

    const usedFields = filters.value.map(filter => filter.field);

    return props.options.filter(option => {
      return !usedFields.includes(option.value);
    });
  });

  const canAddFilter = computed(() => {
    return Boolean(
      selectedOption.value &&
      String(searchValue.value || '').trim()
    );
  });

  const emitFilters = updatedFilters => {
    filters.value = updatedFilters;

    const filtersObject = filtersToObject(updatedFilters);

    emit('input', filtersObject);
    emit('change', filtersObject);

    return filtersObject;
  };

  const resetCurrentFilter = () => {
    selectedField.value = null;
    searchValue.value = '';
  };

  const addFilter = () => {
    if (!canAddFilter.value) {
      return;
    }

    const option = selectedOption.value;

    const newFilter = {
      field: option.value,
      label: option.label,
      value: String(searchValue.value).trim(),
      operator: option.operator || 'contains',
      isArray: option.isArray || false,
    };

    let updatedFilters = [...filters.value];

    if (!props.allowDuplicateFields) {
      updatedFilters = updatedFilters.filter(filter => {
        return filter.field !== newFilter.field;
      });
    }

    updatedFilters.push(newFilter);

    const filtersObject = emitFilters(updatedFilters);
    emit('filter-added', newFilter);
    emit('search', filtersObject);
    resetCurrentFilter();
    setTimeout(() => {
      if(props.showOptions && !hiddenOptionsSelect.value?.open) {
        hiddenOptionsSelect.value?.select()
      }
    }, 100);
  };

  const removeFilter = index => {
    const removedFilter = filters.value[index];

    const updatedFilters = filters.value.filter(
      (_, filterIndex) => filterIndex !== index
    );

    const filtersObject = emitFilters(updatedFilters);

    emit('filter-removed', removedFilter);
    emit('search', filtersObject);
    if(props.showOptions && hiddenOptionsSelect.value?.open) {
      hiddenOptionsSelect.value?.select()
    }
  };

  const clearFilters = () => {
    emitFilters([]);

    emit('clear');
    emit('search', {});

    resetCurrentFilter();
  };

  const onFieldSelected = () => {
    searchValue.value = '';

    requestAnimationFrame(() => {
      inputRef.value?.focus();
    });
  };

  const onValueKeydown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      // setTimeout(() => {
      //   if(!fieldSelector.value?.open) {
      //     fieldSelector.value?.select()
      //     fieldSelector.value?.$refs?.search?.focus();
      //   }
      // }, 100);
      addFilter();
      return;
    }
  
    if (event.key === 'Escape') {
      resetCurrentFilter();
      return;
    }
  
    const isDeleteKey = ['Backspace', 'Delete'].includes(event.key);
    const isInputEmpty = !String(searchValue.value || '').trim();
    if (
      isDeleteKey &&
      isInputEmpty &&
      filters.value.length > 1
    ) {
      event.preventDefault();
      removeFilter(filters.value.length-1);
      return;
    }

    if (
      isDeleteKey &&
      isInputEmpty &&
      selectedField.value?.value && 
      filters.value.length <= 1
    ) {
      event.preventDefault();
      resetCurrentFilter()
    }
  };

  // What Enter would do in the field selector: vue-select selects the option
  // its pointer sits on, and does nothing when the search matches none.
  const fieldSelectorWouldSelect = text => {
    const selector = fieldSelector.value;

    if (selector && Array.isArray(selector.filteredOptions)) {
      return Boolean(selector.filteredOptions[selector.typeAheadPointer]);
    }

    // No selector instance: mirror vue-select's own label filter.
    return availableOptions.value.some(option => {
      return String(option.label || "").toLowerCase().includes(text.toLowerCase());
    });
  };

  // Typing a value without picking a field first: the dropdown filters its
  // options by label, so text that matches no field leaves Enter inert. With a
  // defaultField the text is applied to that field instead of being swallowed.
  const applyDefaultField = () => {
    const text = String(searchValueFieldSelector.value || "").trim();

    if (!props.defaultField || selectedField.value || !text) {
      return false;
    }

    if (fieldSelectorWouldSelect(text)) {
      return false;
    }

    const fallbackOption = props.options.find(option => {
      return option.value === props.defaultField;
    });

    if (!fallbackOption) {
      return false;
    }

    selectedField.value = fallbackOption;
    searchValue.value = text;
    searchValueFieldSelector.value = "";
    addFilter();

    // addFilter clears the picked field within the same tick, so the selector
    // is never re-created and would keep the typed text and its open dropdown.
    if (fieldSelector.value) {
      fieldSelector.value.search = "";
      fieldSelector.value.closeSearchOptions?.();
    }

    return true;
  };

  const onFieldSelectorKeydown = event => {  
    const isDeleteKey = ['Backspace', 'Delete'].includes(event.key);
    if(isDeleteKey && !selectedField.value && !searchValueFieldSelector.value) {
      removeFilter(filters.value.length-1);
      emit('clear');
    }

    if (event.key === 'Enter' && applyDefaultField()) {
      event.preventDefault();
    }
  };

  const onSearchFielSelector = (value) => {
    searchValueFieldSelector.value = value;
  };

  const onClickRemoveFilter = (index) => {
    removeFilter(index);
    emit('clear');
  };



  const handleClickOutside = (event) => {
    if (facetedSerachContainer.value && !facetedSerachContainer.value.contains(event.target)) {
      if(props.showOptions && hiddenOptionsSelect.value?.open) {
        hiddenOptionsSelect.value?.select()
      }
    }
  };

  const onSelectOption = (value) => {
    emit('onSelectOption', value, props.itemIndex);
  };

  const onClickFiltersOptions = () => {
    if(props.showOptions && !hiddenOptionsSelect.value?.open && props.optionsToShow?.length) {
      hiddenOptionsSelect.value?.select()
    }
  };

  const calculatePosition = (dropdownList, component, { width, top, left }) => {
    const dropdownHeight = dropdownList.offsetHeight
    const componentRect = component.$el.getBoundingClientRect()

    dropdownList.style.position = 'absolute'
    dropdownList.style.width = width
    dropdownList.style.left = left

    if(!props.showFieldBottom) {
      dropdownList.style.top = `${window.scrollY + componentRect.bottom}px`
      return
    }

    dropdownList.style.top = `${
      window.scrollY +
      componentRect.top -
      dropdownHeight
    }px`
  }

  return {
    inputRef,
    filters,
    selectedField,
    searchValue,
    selectedOption,
    availableOptions,
    canAddFilter,
    uniqueId,
    hiddenOptionsSelect,
    facetedSerachContainer,
    selectedOptions,
    fieldSelector,
    addFilter,
    onClickRemoveFilter,
    clearFilters,
    onFieldSelected,
    onValueKeydown,
    buildFiltersArray,
    handleClickOutside,
    onSelectOption,
    onClickFiltersOptions,
    onFieldSelectorKeydown,
    onSearchFielSelector,
    calculatePosition
  };
}
